import { useState, useMemo } from "react";
import StatCard from "@/components/base/StatCard";
import EnvironmentalFilters from "./components/EnvironmentalFilters";
import EnvironmentalStatusBadge from "./components/EnvironmentalStatusBadge";
import GreenAreaCard from "./components/GreenAreaCard";
import GreenAreaTable from "./components/GreenAreaTable";
import ConservationUnitCard from "./components/ConservationUnitCard";
import ConservationUnitTable from "./components/ConservationUnitTable";
import EnvironmentalDetailDrawer from "./components/EnvironmentalDetailDrawer";
import NewEnvironmentalAreaModal from "./components/NewEnvironmentalAreaModal";
import {
  areasVerdes,
  unidadesConservacao,
  appsUrbanas,
  areasRecuperacao,
  resumoAmbiental,
} from "@/mocks/ambiental";

type TabKey = "areas-verdes" | "ucs" | "apps" | "recuperacao";

export default function Ambiental() {
  const [activeTab, setActiveTab] = useState<TabKey>("areas-verdes");
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [detailItem, setDetailItem] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIsUC, setModalIsUC] = useState(false);

  const tabData = useMemo(() => {
    let data: any[] = [];
    switch (activeTab) {
      case "areas-verdes": data = areasVerdes; break;
      case "ucs": data = unidadesConservacao; break;
      case "apps": data = appsUrbanas; break;
      case "recuperacao": data = areasRecuperacao; break;
    }

    // Apply filters
    if (Object.keys(filters).length > 0) {
      data = data.filter((item: any) => {
        if (filters.search) {
          const searchable = `${item.nome} ${item.bairro || ""} ${item.responsavel || ""} ${item.categoria || ""} ${item.orgaoGestor || ""} ${item.id}`.toLowerCase();
          if (!searchable.includes(filters.search)) return false;
        }
        if (filters.tipo && item.tipo !== filters.tipo) return false;
        if (filters.status && item.status !== filters.status) return false;
        if (filters.bairro && item.bairro && !item.bairro.toLowerCase().includes(filters.bairro)) return false;
        if (filters.categoria && item.categoria !== filters.categoria) return false;
        if (filters.grupo && item.grupo !== filters.grupo) return false;
        if (filters.statusGestao && item.statusGestao !== filters.statusGestao) return false;
        return true;
      });
    }
    return data;
  }, [activeTab, filters]);

  const openDetail = (item: any) => {
    setDetailItem(item);
    setDrawerOpen(true);
  };

  const openNewModal = (isUC: boolean) => {
    setModalIsUC(isUC);
    setModalOpen(true);
  };

  const tabs: { key: TabKey; label: string; icon: string; count: number }[] = [
    { key: "areas-verdes", label: "Áreas Verdes", icon: "ri-landscape-line", count: resumoAmbiental.areasVerdesCadastradas },
    { key: "ucs", label: "Unidades de Conservação", icon: "ri-shield-star-line", count: resumoAmbiental.ucsCadastradas },
    { key: "apps", label: "APPs Urbanas", icon: "ri-water-flash-line", count: resumoAmbiental.appsUrbanas },
    { key: "recuperacao", label: "Áreas de Recuperação", icon: "ri-heart-pulse-line", count: resumoAmbiental.areasRecuperacao },
  ];

  const isUC = activeTab === "ucs";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1">
          <a href="/" className="hover:text-primary-600 transition-colors">Dashboard</a>
          <i className="ri-arrow-right-s-line text-[10px]"></i>
          <span className="text-foreground-700">Ambiental</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground-900 font-heading">Ambiental</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Gerencie áreas verdes, Unidades de Conservação, APPs urbanas e áreas de recuperação integradas ao CTA.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => openNewModal(false)} className="btn-primary text-xs px-3 py-2 cursor-pointer">
              <i className="ri-add-line"></i>
              Nova Área Verde
            </button>
            <button onClick={() => openNewModal(true)} className="btn-secondary text-xs px-3 py-2 cursor-pointer">
              <i className="ri-add-line"></i>
              Nova UC
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer" title="Importar polígono de área">
              <i className="ri-upload-line"></i>
              Importar Polígono
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer" title="Abrir mapa GIS">
              <i className="ri-map-2-line"></i>
              Abrir Mapa
            </button>
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer" title="Exportar dados">
              <i className="ri-download-line"></i>
              Exportar
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Áreas Verdes Cadastradas"
          value={resumoAmbiental.areasVerdesCadastradas}
          icon="ri-landscape-line"
          iconBg="bg-primary-50"
          iconColor="text-primary-600"
          colorAccent="bg-primary-500"
        />
        <StatCard
          label="UCs Cadastradas"
          value={resumoAmbiental.ucsCadastradas}
          icon="ri-shield-star-line"
          iconBg="bg-accent-50"
          iconColor="text-accent-600"
          colorAccent="bg-accent-500"
        />
        <StatCard
          label="Área Total Mapeada"
          value={`${resumoAmbiental.areaTotalMapeada.toLocaleString("pt-BR")} ha`}
          icon="ri-global-line"
          iconBg="bg-primary-50"
          iconColor="text-primary-600"
          colorAccent="bg-primary-500"
        />
        <StatCard
          label="Árvores em Áreas Ambientais"
          value={resumoAmbiental.arvoresDentroAreasAmbientais}
          icon="ri-plant-line"
          iconBg="bg-accent-50"
          iconColor="text-accent-600"
          colorAccent="bg-accent-500"
        />
        <StatCard
          label="APPs Urbanas"
          value={resumoAmbiental.appsUrbanas}
          icon="ri-water-flash-line"
          iconBg="bg-accent-50"
          iconColor="text-accent-600"
        />
        <StatCard
          label="Áreas em Recuperação"
          value={resumoAmbiental.areasRecuperacao}
          icon="ri-heart-pulse-line"
          iconBg="bg-warning-50"
          iconColor="text-warning-600"
        />
        <StatCard
          label="Risco Crítico em Áreas Ambientais"
          value={resumoAmbiental.riscoCriticoAreasAmbientais}
          icon="ri-error-warning-line"
          iconBg="bg-danger-50"
          iconColor="text-danger-600"
        />
        <StatCard
          label="CO₂e Estimado Preliminar"
          value={`${resumoAmbiental.co2eEstimadoPreliminar.toLocaleString("pt-BR")} t`}
          icon="ri-cloud-line"
          iconBg="bg-primary-50"
          iconColor="text-primary-600"
        />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-card border border-background-200 p-1.5 flex flex-wrap gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setFilters({}); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap cursor-pointer ${
              activeTab === tab.key
                ? "bg-primary-500 text-white shadow-sm"
                : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
            }`}
          >
            <i className={`${tab.icon}`}></i>
            <span>{tab.label}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
              activeTab === tab.key ? "bg-white/20" : "bg-background-200 text-foreground-600"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <EnvironmentalFilters tab={activeTab} onFilter={setFilters} />

      {/* View toggle + Results count */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-foreground-500">
          Exibindo <strong className="text-foreground-700">{tabData.length}</strong> registro{tabData.length !== 1 ? "s" : ""}
          {Object.keys(filters).length > 0 && " (filtrado)"}
        </p>
        <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode("cards")}
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
              viewMode === "cards" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-400 hover:text-foreground-600"
            }`}
            title="Visualização em cards"
          >
            <i className="ri-layout-grid-line"></i>
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
              viewMode === "table" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-400 hover:text-foreground-600"
            }`}
            title="Visualização em tabela"
          >
            <i className="ri-list-check-2"></i>
          </button>
        </div>
      </div>

      {/* Content */}
      {tabData.length === 0 ? (
        <div className="bg-white rounded-xl border border-background-200/70 p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-search-line text-foreground-400 text-2xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-900 mb-1">Nenhum resultado encontrado</h3>
          <p className="text-sm text-foreground-500">Ajuste os filtros ou crie um novo registro.</p>
        </div>
      ) : viewMode === "cards" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tabData.map((item: any) =>
            isUC ? (
              <ConservationUnitCard key={item.id} item={item} onViewDetail={openDetail} />
            ) : (
              <GreenAreaCard key={item.id} item={item} onViewDetail={openDetail} />
            )
          )}
        </div>
      ) : isUC ? (
        <ConservationUnitTable items={tabData} onViewDetail={openDetail} />
      ) : (
        <GreenAreaTable items={tabData} onViewDetail={openDetail} />
      )}

      {/* Detail Drawer */}
      <EnvironmentalDetailDrawer
        open={drawerOpen}
        item={detailItem}
        isUC={
          detailItem
            ? unidadesConservacao.some((uc) => uc.id === detailItem.id)
            : false
        }
        onClose={() => { setDrawerOpen(false); }}
      />

      {/* New Area Modal */}
      <NewEnvironmentalAreaModal
        open={modalOpen}
        isUC={modalIsUC}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}